/**
 * WhatsNewToast — Bottom-right slide-in notification shown once per version.
 *
 * Shows returning users what changed in this version.
 * First-time visitors (no "memba_whats_new_seen" key) are skipped.
 * Dismissing stores APP_VERSION → never shown again for this version.
 *
 * @module components/ui/WhatsNewToast
 */

import { useNetworkNav } from "../../hooks/useNetworkNav"
import { useState, useEffect, useCallback } from "react"
import { APP_VERSION } from "../../lib/config"

const STORAGE_KEY = "memba_whats_new_seen"
const SHOW_DELAY_MS = 3000

/** Check if user is returning (has seen any previous version). */
function shouldShow(): boolean {
    try {
        const seen = localStorage.getItem(STORAGE_KEY)
        // First-time visitor: key doesn't exist → skip
        if (seen === null) return false
        // Returning user who hasn't seen this version → show
        return seen !== APP_VERSION
    } catch {
        return false
    }
}

function dismiss(): void {
    try {
        localStorage.setItem(STORAGE_KEY, APP_VERSION)
    } catch { /* quota */ }
}

/** Initialize the storage key for first-time visitors so the toast only shows on version bumps.
 *  This runs at module load time — safe because Layout imports this module eagerly. */
function initWhatsNewKey(): void {
    try {
        if (localStorage.getItem(STORAGE_KEY) === null) {
            localStorage.setItem(STORAGE_KEY, APP_VERSION)
        }
    } catch { /* SSR */ }
}

// Seed on module load
initWhatsNewKey()

export function WhatsNewToast() {
    const navigate = useNetworkNav()
    const [visible, setVisible] = useState(false)
    const [exiting, setExiting] = useState(false)

    useEffect(() => {
        if (!shouldShow()) return
        const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS)
        return () => clearTimeout(timer)
    }, [])

    const handleDismiss = useCallback(() => {
        setExiting(true)
        setTimeout(() => {
            dismiss()
            setVisible(false)
        }, 200)
    }, [])

    // Escape key dismisses
    useEffect(() => {
        if (!visible) return
        const handler = (e: KeyboardEvent) => {
            if (e.key === "Escape") handleDismiss()
        }
        window.addEventListener("keydown", handler)
        return () => window.removeEventListener("keydown", handler)
    }, [visible, handleDismiss])

    if (!visible) return null

    return (
        <>
            {/* Backdrop — click outside to dismiss */}
            <div
                onClick={handleDismiss}
                style={{
                    position: "fixed", inset: 0, zIndex: 999,
                    background: "rgba(0,0,0,0.2)",
                }}
            />

            {/* Toast */}
            <div
                role="dialog"
                aria-label="What's new in Memba"
                style={{
                    position: "fixed",
                    bottom: 24, right: 24,
                    zIndex: 1000,
                    width: "min(380px, calc(100vw - 32px))",
                    background: "var(--color-bg-card)",
                    backdropFilter: "blur(16px)",
                    border: "1px solid var(--color-primary-border)",
                    borderRadius: 16,
                    padding: "24px 20px 20px",
                    fontFamily: "var(--font-ui, JetBrains Mono, monospace)",
                    boxShadow: "0 8px 32px var(--color-overlay)",
                    animation: exiting
                        ? "membaToastOut 200ms ease-in forwards"
                        : "membaToastIn 300ms ease-out",
                }}
            >
                {/* Close button */}
                <button
                    onClick={handleDismiss}
                    aria-label="Close"
                    style={{
                        position: "absolute", top: 12, right: 14,
                        background: "none", border: "none", color: "var(--color-text-muted)",
                        cursor: "pointer", fontSize: 16, padding: 4,
                        lineHeight: 1,
                    }}
                >
                    ✕
                </button>

                {/* Header */}
                <div style={{ fontSize: "var(--pro-body, 15px)", fontWeight: 700, color: "var(--color-text)", marginBottom: 16 }}>
                    🚀 What's New
                </div>

                {/* Feature entries */}
                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
                    <NetworkItem
                        color="var(--color-brand)"
                        title="AI Governance Analyst"
                        desc="10 free AI models analyze proposals and DAO health from different perspectives."
                    />
                    <NetworkItem
                        color="var(--color-accent-purple-alt)"
                        title="Directory Source Explorer"
                        desc="Browse realm source code, functions, and dependencies — all in-app."
                    />
                    <NetworkItem
                        color="var(--color-accent-teal)"
                        title="Teams & Candidature"
                        desc="Team descriptions, copy invite codes, quest-gated DAO membership."
                    />
                </div>

                {/* Links */}
                <div style={{ fontSize: "var(--pro-caption, 10px)", color: "var(--color-text-secondary)", marginBottom: 14, display: "flex", gap: 12 }}>
                    <span
                        onClick={() => { handleDismiss(); navigate("/settings") }}
                        style={{ color: "var(--color-text-secondary)", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 2 }}
                    >
                        Settings ⚙
                    </span>
                    <span
                        onClick={() => { handleDismiss(); navigate("/changelogs") }}
                        style={{ color: "var(--color-text-secondary)", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 2 }}
                    >
                        Full changelog →
                    </span>
                </div>

                {/* Dismiss button */}
                <button
                    onClick={handleDismiss}
                    autoFocus
                    style={{
                        width: "100%",
                        padding: "10px 0",
                        borderRadius: 10,
                        border: "1px solid rgba(0,212,170,0.2)",
                        background: "rgba(0,212,170,0.08)",
                        color: "var(--color-primary)",
                        cursor: "pointer",
                        fontFamily: "var(--font-ui, JetBrains Mono, monospace)",
                        fontSize: "var(--pro-small, 12px)", fontWeight: 600,
                        transition: "background 0.15s",
                    }}
                    onMouseOver={e => (e.currentTarget.style.background = "rgba(0,212,170,0.15)")}
                    onMouseOut={e => (e.currentTarget.style.background = "rgba(0,212,170,0.08)")}
                >
                    Got it ✓
                </button>
            </div>

            {/* Animations */}
            <style>{`
                @keyframes membaToastIn {
                    from { opacity: 0; transform: translateY(20px) scale(0.97); }
                    to   { opacity: 1; transform: translateY(0) scale(1); }
                }
                @keyframes membaToastOut {
                    from { opacity: 1; transform: translateY(0) scale(1); }
                    to   { opacity: 0; transform: translateY(12px) scale(0.97); }
                }
                @media (prefers-reduced-motion: reduce) {
                    @keyframes membaToastIn { from { opacity: 0; } to { opacity: 1; } }
                    @keyframes membaToastOut { from { opacity: 1; } to { opacity: 0; } }
                }
                @media (max-width: 640px) {
                    [role="dialog"][aria-label="What's new in Memba"] {
                        bottom: 0 !important;
                        right: 0 !important;
                        width: 100% !important;
                        border-radius: 16px 16px 0 0 !important;
                    }
                }
            `}</style>
        </>
    )
}

function NetworkItem({ color, title, desc }: { color: string; title: string; desc: string }) {
    return (
        <div style={{
            padding: "10px 12px",
            borderRadius: 10,
            background: "var(--color-surface)",
            borderLeft: `3px solid ${color}`,
        }}>
            <div style={{ fontSize: "var(--pro-caption, 11px)", fontWeight: 600, color: "var(--color-text)", marginBottom: 2 }}>
                ▸ {title}
            </div>
            <div style={{ fontSize: "var(--pro-caption, 10px)", color: "var(--color-text-secondary)", lineHeight: 1.4 }}>
                {desc}
            </div>
        </div>
    )
}
